/**
 * 员工使用分析页（/admin/usage）—— CEO 与授权管理层查看全体员工真实使用情况。
 *
 * ============================== 需求背景（2026-10-20 改版） ==============================
 * 老板的明确要求：**这里只要一个用户列表**，把需要的数据直接展示在列表上；
 * 点某一行后，抽屉里显示的也只是一个列表 —— 就是「这个人具体访问了什么页面、
 * 做了什么操作」的逐条记录。
 *
 * 所以本页不再有 KPI 卡片、趋势图、热力图、模块排行、组织维度等区块，
 * 只保留「筛选条 + 一张员工表」。原因很直接：老板看这份数据时的问题是
 * 「谁在用、用了多久、谁很久没用了」，一行一个人正好回答；图表反而把答案埋起来。
 *
 * 逐条操作明细仍在抽屉里（分页），入口就在每一行上。
 *
 * ============================== 统计口径 ==============================
 * 全部数字取自 GET /api/usage/overview 一次返回的 employees，前端**不做二次口径计算**
 * （只做展示格式化），保证列表、导出、抽屉用的是同一份后端统计：
 *   · 有效使用时长 = 前台可见 && 最近 5 分钟有操作（后台 / 锁屏 / 挂机不计）；
 *   · 登录时长与有效时长分列，默认排序看有效时长；
 *   · admin / CEO 本人不计入统计（后端 exclusion_reason 恒定排除）。
 * 接口同时会返回趋势 / 模块等区块数据，本页不再渲染，但复用同一个接口可以避免
 * 另开一套统计口径导致「列表数字和别人看到的数字对不上」。
 *
 * ============================== 权限 ==============================
 * · admin / CEO：全公司；
 * · 部门经理：本部门（销售线加本分公司）；
 * · 普通员工：即使被单独授权进入，也只能看到自己（后端 usage_scope 收窄）。
 * 抽屉每次翻页都会写审计日志（查看人 / 时间 / 筛选范围）。
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, BarChart3, ChevronRight, Clock, Download, Eye, FileSpreadsheet,
  Info, Loader2, RefreshCw, Search, ShieldCheck,
} from "lucide-react";
import * as XLSX from "xlsx";
import { useApp } from "../../context/AppContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "../../components/ui/sonner";
import { UsageEmployeeDrawer } from "../../components/usage/UsageEmployeeDrawer";
import { fmtDuration, fmtNumber, shortModuleName } from "../../lib/usageFormat";

/* 时间范围选项：与后端 resolve_range 的 key 一一对应。 */
const RANGES = [
  { key: "today", zh: "今天", en: "Today" },
  { key: "7d", zh: "近 7 天", en: "7 days" },
  { key: "30d", zh: "近 30 天", en: "30 days" },
  { key: "90d", zh: "近 90 天", en: "90 days" },
  { key: "custom", zh: "自定义", en: "Custom" },
];

/* 活跃状态筛选。三档「未使用」与需求第八节的 7 / 14 / 30 天一致；
   口径是「以此刻为基准的连续未使用天数」，不随上方时间范围变化。 */
const ACTIVITY = [
  { key: "all", zh: "全部", en: "All" },
  { key: "today", zh: "今日活跃", en: "Active today" },
  { key: "idle7", zh: "7 天未使用", en: "Idle 7d+" },
  { key: "idle14", zh: "14 天未使用", en: "Idle 14d+" },
  { key: "idle30", zh: "30 天未使用", en: "Idle 30d+" },
];

/* 排序依据：默认「日均有效使用时长」，避免总时长被「谁在线时间长」带偏。 */
const METRICS = [
  { key: "daily", zh: "日均有效时长", en: "Daily avg" },
  { key: "total", zh: "总有效时长", en: "Total" },
  { key: "days", zh: "活跃天数", en: "Active days" },
  { key: "sessions", zh: "会话数", en: "Sessions" },
  { key: "actions", zh: "业务动作数", en: "Actions" },
  { key: "modules", zh: "使用模块数", en: "Modules" },
];

export default function UsageAnalytics() {
  const { lang, api } = useApp();
  const zh = lang === "zh";

  const [range, setRange] = useState("30d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [tz, setTz] = useState("Asia/Shanghai");
  const [department, setDepartment] = useState("");
  const [role, setRole] = useState("");
  const [branch, setBranch] = useState("");
  const [search, setSearch] = useState("");
  const [activity, setActivity] = useState("all");
  const [metric, setMetric] = useState("daily");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  /* 被点开的员工：存整行而不是只存用户名 —— 抽屉头部要显示姓名 / 部门，
     直接从行数据取就不用再发一次员工详情请求，也少写一条重复的审计。 */
  const [detail, setDetail] = useState(null);

  const params = useMemo(() => ({
    range, tz, department, role, branch, search, activity, metric,
    ...(range === "custom" ? { start: customStart, end: customEnd } : {}),
  }), [range, tz, department, role, branch, search, activity, metric, customStart, customEnd]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: d } = await api.get("/usage/overview", { params });
      setData(d);
    } catch (e) {
      setError(e?.detailText || (zh ? "加载失败，请稍后重试" : "Failed to load"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api, params, zh]);

  useEffect(() => {
    // 自定义范围没填完不发请求：避免半填日期被后端当成「无效区间」而整页报错。
    if (range === "custom" && (!customStart || !customEnd)) { setLoading(false); return; }
    load();
  }, [load, range, customStart, customEnd]);

  const meta = data?.meta;
  const kpi = data?.kpi;
  const tzLabel = meta?.timezones?.[tz] ? (zh ? meta.timezones[tz].zh : meta.timezones[tz].en) : tz;

  /* 列表直接用后端返回的 `ranking`（已按所选 metric 用 rank_rows 排好序、带名次）：
     为什么不用 employees —— 它是原始顺序，没排序也没名次；而 ranking 与旧版排名区块、
     导出、leaderboard 卡共用同一套 rank_rows 口径，列表顺序才和别处对得上。
     样本不足的人排在合格样本之后但仍保留，不隐藏事实。 */
  const rows = data?.ranking || [];

  /* ---------------- 导出：当前筛选结果（并写审计日志） ---------------- */
  const exportExcel = async () => {
    if (!data) return;
    const sheet = rows.map((r) => ({
      [zh ? "员工" : "Employee"]: r.name,
      [zh ? "账号" : "Account"]: r.username,
      [zh ? "部门" : "Department"]: r.departmentName,
      [zh ? "分公司" : "Branch"]: r.branchName,
      [zh ? "角色" : "Role"]: r.roleLabel,
      [zh ? "有效时长(分钟)" : "Effective (min)"]: Math.round(r.effSeconds / 60),
      [zh ? "登录时长(分钟)" : "Login (min)"]: Math.round(r.loginSeconds / 60),
      [zh ? "日均有效时长(分钟)" : "Daily avg (min)"]: Math.round(r.avgDailyEffSec / 60),
      [zh ? "活跃天数" : "Active days"]: r.activeDays,
      [zh ? "会话数" : "Sessions"]: r.sessions,
      [zh ? "业务动作数" : "Actions"]: r.actionTotal,
      [zh ? "使用模块数" : "Modules"]: r.moduleCount,
      [zh ? "主要模块" : "Top module"]: r.topModule || "",
      [zh ? "最后活跃" : "Last active"]: r.lastActiveAt ? new Date(r.lastActiveAt).toLocaleString() : "",
      [zh ? "连续未使用天数" : "Idle days"]: r.idleDays ?? "",
    }));
    const ws = XLSX.utils.json_to_sheet(sheet);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, zh ? "员工使用" : "Usage");
    XLSX.writeFile(wb, `usage_${range}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    try {
      await api.post("/usage/audit-export", { kind: "ranking", filters: params, rows: sheet.length });
    } catch { /* 审计失败不阻断导出 */ }
    toast.success(zh ? "已导出当前筛选结果" : "Exported current filters");
  };

  /* page-fill：页面根撑满内容区剩余高度（见 index.css 说明）。
     配合下方列表卡片的 card-fill，让**只有表格自己滚动**，页面不出全局滚动条 ——
     这是设计部项目管理列表（DesignV3Projects.jsx:778）的既定做法：
       · .page-fill 让页面根成为纵向弹性容器并撑满 .app-page-content；
       · .card-fill 让卡片吃掉剩余高度、内部双轴自滚；
     若不用这套，卡片就需要写死 max-h-[calc(100vh-15rem)]：
     视口一高一矮，全局滚动条和卡片滚动条就会同时出现（2026-10-20 实测双滚动条）。 */
  return (
    <div className="page-fill space-y-4" data-testid="usage-analytics-page">
      <h1 className="sr-only">{zh ? "员工使用分析" : "Employee usage analytics"}</h1>

      {/* ============ 筛选条：时间范围 / 时区 / 部门 / 角色 / 分公司 / 活跃状态 / 搜索 ============
          shrink-0：页面根是 .page-fill 弹性容器，筛选条必须固定高度、不许被挤压，
          剩余空间全部留给下面的列表卡片（否则窗口一矮，筛选条会被压变形）。 */}
      <div className="shrink-0 rounded-xl border border-slate-200 bg-white p-3 sm:p-4" data-testid="usage-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-lg border border-slate-200" role="group" aria-label={zh ? "时间范围" : "Date range"}>
            {RANGES.map((r) => (
              <button key={r.key} type="button" data-testid={`usage-range-${r.key}`} onClick={() => setRange(r.key)}
                aria-pressed={range === r.key}
                className={`min-h-9 px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${range === r.key ? "bg-brand-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {zh ? r.zh : r.en}
              </button>
            ))}
          </div>
          {range === "custom" && (
            <div className="flex items-center gap-1.5">
              <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="h-9 w-[140px] text-xs" data-testid="usage-custom-start" aria-label={zh ? "开始日期" : "Start date"} />
              <span className="text-xs text-slate-400">~</span>
              <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="h-9 w-[140px] text-xs" data-testid="usage-custom-end" aria-label={zh ? "结束日期" : "End date"} />
            </div>
          )}

          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger className="h-9 w-[190px] text-xs" data-testid="usage-tz-select" aria-label={zh ? "时区" : "Timezone"}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(meta?.timezones || { "Asia/Shanghai": { zh: "中国时间（UTC+8）", en: "China Time" }, "America/Los_Angeles": { zh: "美国太平洋时间", en: "US Pacific" } })
                .map(([key, label]) => <SelectItem key={key} value={key}>{zh ? label.zh : label.en}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={department || "all"} onValueChange={(v) => setDepartment(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-[132px] text-xs" data-testid="usage-department-select" aria-label={zh ? "部门" : "Department"}><SelectValue placeholder={zh ? "部门" : "Department"} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{zh ? "全部部门" : "All departments"}</SelectItem>
              {(meta?.departments || []).map((d) => <SelectItem key={d.id} value={d.id}>{zh ? d.name : d.nameEn}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={role || "all"} onValueChange={(v) => setRole(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-[132px] text-xs" data-testid="usage-role-select" aria-label={zh ? "角色" : "Role"}><SelectValue placeholder={zh ? "角色" : "Role"} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{zh ? "全部角色" : "All roles"}</SelectItem>
              {(meta?.roles || []).map((d) => <SelectItem key={d.id} value={d.id}>{zh ? d.name : d.nameEn}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={branch || "all"} onValueChange={(v) => setBranch(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-[132px] text-xs" data-testid="usage-branch-select" aria-label={zh ? "分公司" : "Branch"}><SelectValue placeholder={zh ? "分公司" : "Branch"} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{zh ? "全部分公司" : "All branches"}</SelectItem>
              {(meta?.branches || []).map((d) => <SelectItem key={d.id} value={d.id}>{zh ? d.name : d.nameEn}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={activity} onValueChange={setActivity}>
            <SelectTrigger className="h-9 w-[140px] text-xs" data-testid="usage-activity-select" aria-label={zh ? "活跃状态" : "Activity"}><SelectValue /></SelectTrigger>
            <SelectContent>
              {ACTIVITY.map((a) => <SelectItem key={a.key} value={a.key}>{zh ? a.zh : a.en}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="h-9 w-[150px] text-xs" data-testid="usage-metric-select" aria-label={zh ? "排序依据" : "Sort by"}><SelectValue /></SelectTrigger>
            <SelectContent>
              {METRICS.map((m) => <SelectItem key={m.key} value={m.key}>{zh ? m.zh : m.en}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={zh ? "搜索员工" : "Search employee"}
              className="h-9 w-[160px] pl-8 text-xs" data-testid="usage-search" aria-label={zh ? "搜索员工" : "Search employee"} />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-medium text-brand-700" data-testid="usage-tz-badge">
              <Clock className="size-3" aria-hidden="true" />{tzLabel}
            </span>
            <button type="button" onClick={load} data-testid="usage-refresh"
              className="inline-flex size-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition-colors hover:border-brand-300 hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              aria-label={zh ? "刷新" : "Refresh"} title={zh ? "刷新" : "Refresh"}>
              <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
            </button>
            <Button type="button" variant="outline" size="sm" onClick={exportExcel} disabled={!data} data-testid="usage-export"
              className="h-9 gap-1.5 text-xs">
              <FileSpreadsheet className="size-3.5" aria-hidden="true" />{zh ? "导出" : "Export"}
            </Button>
          </div>
        </div>

        {/* 口径提示：老板最容易误读的地方（admin/CEO 排除、历史数据不可还原） */}
        <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1">
            <ShieldCheck className="size-3 text-emerald-500" aria-hidden="true" />
            {zh ? `已排除 admin / CEO ${meta?.excluded?.exec ?? 0} 人、外部账号 ${meta?.excluded?.external ?? 0} 人`
              : `Excluded ${meta?.excluded?.exec ?? 0} admin/CEO, ${meta?.excluded?.external ?? 0} external accounts`}
          </span>
          {(meta?.excluded?.disabled > 0 || meta?.excluded?.test > 0) && (
            <span>{zh ? `停用账号 ${meta?.excluded?.disabled ?? 0} 人、测试 / 系统账号 ${(meta?.excluded?.test ?? 0) + (meta?.excluded?.system ?? 0)} 人已排除`
              : `${meta?.excluded?.disabled ?? 0} disabled, ${(meta?.excluded?.test ?? 0) + (meta?.excluded?.system ?? 0)} test/system excluded`}</span>
          )}
          {meta?.preciseSince && (
            <span className="inline-flex items-center gap-1 text-amber-700" data-testid="usage-precise-since">
              <Info className="size-3" aria-hidden="true" />
              {zh ? `精确统计自 ${new Date(meta.preciseSince).toLocaleDateString()} 启用，更早的数据不可还原`
                : `Precise tracking since ${new Date(meta.preciseSince).toLocaleDateString()}`}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Eye className="size-3" aria-hidden="true" />{zh ? "不采集输入内容、聊天正文与文件内容" : "No input, chat or file content is collected"}
          </span>
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="usage-error" role="alert">
          <span className="inline-flex items-center gap-2"><AlertTriangle className="size-4" aria-hidden="true" />{error}</span>
          <Button type="button" size="sm" variant="outline" onClick={load}>{zh ? "重试" : "Retry"}</Button>
        </div>
      )}

      {loading && !data && (
        <div className="flex min-h-64 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400" data-testid="usage-loading">
          <Loader2 className="size-5 animate-spin" aria-hidden="true" />
        </div>
      )}

      {!loading && !data && !error && range === "custom" && (!customStart || !customEnd) && (
        <div className="flex min-h-40 flex-col items-center justify-center rounded-xl border border-slate-200 bg-white text-center" data-testid="usage-custom-hint">
          <BarChart3 className="size-5 text-slate-300" aria-hidden="true" />
          <p className="mt-2 text-sm text-slate-500">{zh ? "请选择完整的自定义日期范围" : "Pick both a start and an end date"}</p>
        </div>
      )}

      {/* ============ 唯一的用户列表 ============
          一行一个人，需要的数据都在列上。点任意一行进入抽屉看逐条操作记录。
          表格横向滚动只发生在卡片内部，页面本身不横向滚动。 */}
      {data && (
        /* card-fill：卡片吃掉 page-fill 的剩余高度并内部滚动（只在有数据时挂，
           因为空态卡片没有可滚内容，挂上会被压扁）。
           这是「单滚动条」的关键 —— 页面本身不再滚动，只有表格区域内滚。 */
        <section className="card-fill flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="usage-ranking">
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-900">{zh ? "员工使用列表" : "Employee usage"}</h2>
              <p className="mt-0.5 text-xs text-slate-500">
                {zh
                  ? `共 ${fmtNumber(kpi.totalEmployees, zh)} 人 · 期间活跃 ${fmtNumber(kpi.activeEmployees, zh)} 人 · 点击任意一行查看具体操作记录`
                  : `${fmtNumber(kpi.totalEmployees, zh)} employees · ${fmtNumber(kpi.activeEmployees, zh)} active · click a row for the activity log`}
              </p>
            </div>
            <span className="text-[11px] text-slate-400">
              {zh ? `排序：${METRICS.find((m) => m.key === metric)?.zh || ""}` : `Sort: ${METRICS.find((m) => m.key === metric)?.en || ""}`}
            </span>
          </div>

          {rows.length === 0 ? (
            <div className="flex min-h-40 flex-col items-center justify-center px-4 py-8 text-center" data-testid="usage-ranking-empty">
              <BarChart3 className="size-5 text-slate-300" aria-hidden="true" />
              <p className="mt-2 text-sm text-slate-500">{zh ? "没有符合条件的员工" : "No employees match the filters"}</p>
              <p className="mt-0.5 text-xs text-slate-400">{zh ? "试试放宽部门、角色或活跃状态筛选" : "Try relaxing dept, role or activity filters"}</p>
            </div>
          ) : (
            /* 表头固定 + 单滚动条（需求 2026-10-20）：
                 ① 这层 div 用 flex-1 + min-h-0 吃掉卡片剩余高度并自己滚动
                    —— 不再写 max-h-[calc(100vh-15rem)] 这类视口减法常量；
                 ② thead 的 sticky top-0 吸附的正是这层滚动容器；
                 ③ 卡片本身 overflow-hidden，页面根 / 内容区都不产生滚动条。
               之前那版只给这层加了 max-h，结果页面还有一个全局滚动条：
               卡片被撑到视口高度、页面也跟着滚，于是出现「一个全局 + 一个表格」双滚动条。 */
            <div className="min-h-0 flex-1 overflow-auto" tabIndex={0} role="region"
              aria-label={zh ? "员工使用列表，可横向滚动" : "Employee usage table, horizontally scrollable"}>
              {/* min-w 取 1040：13 列在 1440 视口的可用宽度（扣除侧栏约 1134px）里刚好放得下，
                  不会一进来就要横向拖；更窄的视口只在卡片内部滚动，页面本身不滚动。 */}
              <table className="w-full min-w-[1040px] text-xs" data-testid="usage-ranking-table">
                {/* 表头固定：sticky 与背景色都必须写在 <th> 上，不能只写在 <thead>/<tr> 上。
                    为什么（2026-10-20 实测）：`border-collapse` 下 <tr> 不建立独立的绘制层，
                    滚动时行内容会从表头与首行之间那条缝里露出来（截图里能看到半行「Aden」）。
                    设计部项目列表（DesignV3Projects.jsx:700）就是按「th 上 sticky + bg」做的，
                    这里对齐同一写法：每个 th 自带 sticky top-0 + 不透明底色 + 下边框。 */}
                <thead>
                  <tr className="text-left text-[11px] text-slate-500">
                    <th className="sticky top-0 z-10 w-10 border-b border-slate-200 bg-slate-50 py-2.5 pl-4 pr-2 font-medium">#</th>
                    <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 py-2.5 pr-3 font-medium">{zh ? "员工" : "Employee"}</th>
                    <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 py-2.5 pr-3 font-medium">{zh ? "部门" : "Dept"}</th>
                    <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 py-2.5 pr-3 text-right font-medium">{zh ? "有效时长" : "Effective"}</th>
                    <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 py-2.5 pr-3 text-right font-medium">{zh ? "日均" : "Daily avg"}</th>
                    <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 py-2.5 pr-3 text-right font-medium">{zh ? "登录时长" : "Login"}</th>
                    <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 py-2.5 pr-3 text-right font-medium">{zh ? "活跃天数" : "Active days"}</th>
                    <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 py-2.5 pr-3 text-right font-medium">{zh ? "会话数" : "Sessions"}</th>
                    <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 py-2.5 pr-3 text-right font-medium">{zh ? "业务动作" : "Actions"}</th>
                    <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 py-2.5 pr-3 text-right font-medium">{zh ? "模块数" : "Modules"}</th>
                    <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 py-2.5 pr-3 font-medium">{zh ? "主要模块" : "Top module"}</th>
                    <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 py-2.5 pr-3 text-right font-medium">{zh ? "最后活跃" : "Last active"}</th>
                    <th className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 py-2.5 pr-3 text-right font-medium">{zh ? "未使用" : "Idle"}</th>
                    <th className="sticky top-0 z-10 w-8 border-b border-slate-200 bg-slate-50 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.username}
                      className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-brand-50/40 focus-within:bg-brand-50/40"
                      data-testid={`usage-rank-row-${r.username}`}
                      tabIndex={0} role="button"
                      aria-label={zh ? `查看 ${r.name} 的操作记录` : `View ${r.name}'s activity`}
                      onClick={() => setDetail(r)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetail(r); } }}>
                      <td className="py-2.5 pl-4 pr-2 tabular-nums text-slate-400">{i + 1}</td>
                      <td className="py-2.5 pr-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-medium text-slate-800">{r.name}</span>
                          {/* 「样本不足」只在真正需要提醒时出现：
                              后台口径是「活跃天数 < 阈值」，但列表里绝大多数人本来就只有一两天，
                              每行都挂标签会让整张表变成一片黄色噪音。
                              所以这里只对「区间内完全没用过」（effSeconds=0）的人保留提示，
                              其余人看右侧「未使用」列即可 —— 那列已经用琥珀色标出 ≥7 天。 */}
                          {r.sampleInsufficient && r.effSeconds === 0 && (
                            <span className="rounded bg-slate-100 px-1 py-0.5 text-[10px] font-medium text-slate-500" data-testid={`usage-rank-insufficient-${r.username}`}
                              title={zh ? "所选区间内没有有效使用记录" : "No effective usage in range"}>
                              {zh ? "区间内未使用" : "unused"}
                            </span>
                          )}
                          {r.changePct != null && r.changePct <= -50 && (
                            <span className="rounded bg-red-50 px-1 py-0.5 text-[10px] font-medium text-red-600"
                              title={zh ? "相比上一周期下降超过一半" : "Down more than half vs previous period"}>
                              {r.changePct}%
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 font-mono text-[10px] text-slate-400">{r.username}</p>
                      </td>
                      <td className="py-2.5 pr-3 text-slate-600">{r.departmentName || "—"}</td>
                      <td className="py-2.5 pr-3 text-right font-semibold tabular-nums text-slate-800">{fmtDuration(r.effSeconds, zh)}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-slate-600">{fmtDuration(r.avgDailyEffSec, zh)}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-slate-500">{fmtDuration(r.loginSeconds, zh)}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-slate-600">{r.activeDays}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-slate-600">{fmtNumber(r.sessions, zh)}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-slate-600">{fmtNumber(r.actionTotal, zh)}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-slate-600">{r.moduleCount}</td>
                      <td className="max-w-[180px] truncate py-2.5 pr-3 text-slate-600" title={r.topModuleName || ""}>{shortModuleName(r.topModuleName, 14) || "—"}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-slate-500">{r.lastActiveAt ? new Date(r.lastActiveAt).toLocaleDateString() : "—"}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">
                        {r.idleDays == null ? <span className="text-slate-300">—</span> : (
                          <span className={r.idleDays >= 7 ? "font-semibold text-amber-700" : "text-slate-500"}>
                            {zh ? `${r.idleDays} 天` : `${r.idleDays}d`}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5"><ChevronRight className="size-3.5 text-slate-300" aria-hidden="true" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* 底部说明条固定不滚动（shrink-0）：隐私口径要一直能看到，不该被滚走。 */}
          <p className="shrink-0 border-t border-slate-100 px-4 py-2.5 text-[10px] leading-4 text-slate-400">{meta?.privacy}</p>
        </section>
      )}

      {/* 逐条使用情况抽屉：里面也只是一个列表（分页），每次翻页写审计日志 */}
      <UsageEmployeeDrawer
        username={detail?.username || ""}
        employee={detail}
        range={range}
        start={customStart}
        end={customEnd}
        tz={tz}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}
